import { Chart } from 'chart.js';

// Shared styling + helpers for the company-view quarterly category charts
// (innovation score history, signal/stock-performance history, …).
// Chart-specific rules (axis ranges, thresholds, markers, tooltips) stay in each component.

export const FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export const CHART_COLOR = {
  axisText: '#0f172a',
  axisTitle: '#0f172a',
  tooltipBg: '#ffffff',
  tooltipTitle: '#64748b',
  tooltipBody: '#0f172a',
  tooltipBorder: 'rgba(15, 23, 42, 0.08)',
  grid: 'rgba(15, 23, 42, 0.12)',
  plotBg: 'rgba(15, 23, 42, 0.04)',
  scoreLine: '#1e293b',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const QUARTER: Record<string, string> = { '03': 'Q1', '06': 'Q2', '09': 'Q3', '12': 'Q4' };

// x-axis tick label: Q4 carries the year ("Q4 2015"); other quarters "Q1/Q2/Q3"; non-quarter months "Mar".
export const axisLabel = (dateEom: string): string => {
  const mm = dateEom.slice(5, 7);
  if (mm === '12') return `Q4 ${dateEom.slice(0, 4)}`;
  return QUARTER[mm] ?? MONTHS[+mm - 1];
};

// Full label for tooltip titles: "Q4 2015" / "Mar 2015".
export const periodLabel = (dateEom: string): string => {
  const mm = dateEom.slice(5, 7);
  return `${QUARTER[mm] ?? MONTHS[+mm - 1]} ${dateEom.slice(0, 4)}`;
};

export type TimeRange = '10y' | '3y' | '1y';

// 1Y: last 12 months as-is. 3Y/10Y: quarter-end points only (source mixes monthly + quarterly,
// this guarantees uniform spacing), always keeping the final point as the live data tip.
export function filterQuarterly<T extends { date_eom: string }>(history: T[], range: TimeRange): T[] {
  if (!history.length) return history;
  let data = history;

  if (range === '1y') {
    const cutoff = new Date(data[data.length - 1].date_eom);
    cutoff.setFullYear(cutoff.getFullYear() - 1);
    return data.filter(p => new Date(p.date_eom) >= cutoff);
  }
  if (range === '3y') {
    const cutoff = new Date(data[data.length - 1].date_eom);
    cutoff.setFullYear(cutoff.getFullYear() - 3);
    data = data.filter(p => new Date(p.date_eom) >= cutoff);
  }

  const Q = new Set([3, 6, 9, 12]);
  const last = data[data.length - 1];
  const quarterly = data.filter(p => Q.has(new Date(p.date_eom).getMonth() + 1));
  if (!Q.has(new Date(last.date_eom).getMonth() + 1)) quarterly.push(last);
  return quarterly;
}

export const hexWithAlpha = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// White canvas behind everything — needed so PNG/PDF exports aren't transparent.
export const whiteBackgroundPlugin = {
  id: 'chartWhiteBg',
  beforeDraw(chart: Chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

// Light grey fill behind the plot area (drawn before bars/line, above the white canvas).
export const chartAreaBgPlugin = {
  id: 'chartAreaBg',
  beforeDatasetsDraw(chart: Chart) {
    const { ctx, chartArea: { left, top, right, bottom } } = chart;
    ctx.save();
    ctx.fillStyle = CHART_COLOR.plotBg;
    ctx.fillRect(left, top, right - left, bottom - top);
    ctx.restore();
  },
};

// Category x-axis: one tick+gridline per data point, 45° labels, gridlines through
// category centers (offset:false) and behind the bars (z:-1).
export const categoryXScale = (): any => ({
  type: 'category',
  ticks: {
    autoSkip: false,
    maxRotation: 45,
    minRotation: 45,
    font: { family: FONT_FAMILY, size: 11 },
    color: CHART_COLOR.axisText,
    padding: 8,
  },
  grid: { display: true, color: CHART_COLOR.grid, offset: false, z: -1 },
  border: { display: false },
});

// Shared tooltip chrome; pass chart-specific `callbacks`.
export const baseTooltip = (callbacks: any): any => ({
  backgroundColor: CHART_COLOR.tooltipBg,
  titleColor: CHART_COLOR.tooltipTitle,
  titleFont: { family: FONT_FAMILY, size: 13, weight: 500 },
  bodyColor: CHART_COLOR.tooltipBody,
  bodyFont: { family: FONT_FAMILY, size: 14, weight: 600 },
  padding: 12,
  borderColor: CHART_COLOR.tooltipBorder,
  borderWidth: 1,
  displayColors: true,
  cornerRadius: 8,
  caretSize: 0,
  callbacks,
});

// Drag-to-zoom / shift-pan for a category axis (index-based limits).
export const categoryZoom = (): any => ({
  limits: { x: { min: 'original', max: 'original', minRange: 2 } },
  pan: { enabled: true, mode: 'x', modifierKey: 'shift' },
  zoom: {
    wheel: { enabled: false },
    pinch: { enabled: true },
    drag: {
      enabled: true,
      backgroundColor: 'rgba(99, 102, 241, 0.15)',
      borderColor: 'rgba(99, 102, 241, 0.6)',
      borderWidth: 1,
    },
    mode: 'x',
  },
});

// Options common to every category chart; spread then add `plugins` + `scales`.
export const COMMON_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  layout: { padding: { top: 24, right: 32, bottom: 8, left: 8 } },
  animation: { duration: 800, easing: 'easeOutQuart' },
};
